import { useEffect, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'

import Btn from '../../../../UI/Btn'
import Checkbox from '../../../../UI/Checkbox'
import Loading from '../../../../UI/Loading'
import { PlainTextarea } from '../../../../UI/PlainField'
import Card from './Card'

import styles from './editModal.module.scss'
import { formatDateTime } from '../../../../shared/datetime'

/**
 * Reiter „Log-Verlauf".
 *
 * Eine Zeitleiste: die automatisch mitgeschriebenen Ereignisse (registriert,
 * freigegeben, Rollen geändert, Anfrage einer Junior Company, Zusage zu einem
 * Bedarf …) und die Notizen von Verwaltung und Person. Beides in einer Liste —
 * zwei getrennte Listen zwängen zum Hin- und Herspringen, um zu verstehen, was
 * wann passiert ist.
 *
 * Eine Notiz der Verwaltung bleibt intern, solange der Haken „für die
 * Unterstützer:in sichtbar" nicht gesetzt ist. Eine Benachrichtigung löst sie
 * bewusst nicht aus: Wer etwas mitteilen will, nimmt dafür die Nachrichten.
 */
export default function SupporterLogTab({ supporterId }) {
	const { t } = useTranslation()

	const [entries, setEntries] = useState(null)
	const [message, setMessage] = useState('')
	const [visibleToSupporter, setVisible] = useState(false)
	const [isSaving, setSaving] = useState(false)

	const load = () =>
		http
			.get(TApi.ADMIN_SUPPORTER_LOG(supporterId))
			.then(({ data }) => setEntries(data))
			.catch(() => setEntries([])) // Meldung kommt aus shared/http

	useEffect(() => {
		if (!supporterId) return

		setEntries(null)
		load()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supporterId])

	const submit = () => {
		const text = message.trim()
		if (!text) return

		setSaving(true)
		http.post(TApi.ADMIN_SUPPORTER_LOG(supporterId), { message: text, visibleToSupporter })
			.then(() => {
				setMessage('')
				setVisible(false)
				toast.success(t('Entry saved'))
				return load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	return (
		<>
			<Card title={t('newLogEntry')} hint={t('supporterLogNoteHint')}>
				<PlainTextarea
					rows={4}
					placeholder={t('supporterLogNotePlaceholder')}
					value={message}
					onChange={e => setMessage(e.target.value)}
				/>
				<div className={styles.logSubmitRow}>
					<Checkbox
						label={t('visibleToSupporter')}
						register={{
							checked: visibleToSupporter,
							onChange: () => setVisible(value => !value)
						}}
					/>
					<Btn loading={isSaving} disabled={!message.trim()} type="button" onClick={submit}>
						{t('save')}
					</Btn>
				</div>
			</Card>

			<Card title={t('logHistory')}>
				{entries === null && <Loading />}
				{entries?.length === 0 && <p className={styles.hint}>{t('noLogEntriesYet')}</p>}
				{!!entries?.length && (
					<ol className={styles.timeline}>
						{entries.map(entry => {
							const isEvent = entry.type === 'EVENT'

							return (
								<li key={entry.id} className={styles.timelineItem}>
									<span
										className={clsx(
											styles.timelineDot,
											isEvent ? styles.timelineDotEvent : styles.timelineDotNote
										)}
									/>
									<div className={styles.timelineBody}>
										<div className={styles.timelineHead}>
											<span className={styles.timelineTitle}>
												{isEvent
													? t(`supporterLogEvent${entry.eventKey}`, entry.eventKey)
													: t(
															entry.authorRole === 'SUPPORTER'
																? 'noteBySupporter'
																: 'noteByAdmin'
													  )}
											</span>
											<span className={styles.timelineMeta}>
												{formatDateTime(entry.createdAt, {
													day: '2-digit',
													month: '2-digit',
													year: 'numeric',
													hour: '2-digit',
													minute: '2-digit'
												})}
												{entry.authorName && ` · ${entry.authorName}`}
											</span>
										</div>
										{entry.message && <p className={styles.timelineText}>{entry.message}</p>}
										{!isEvent && (
											<div className={styles.timelineTags}>
												<span
													className={clsx(
														styles.timelineTag,
														!entry.visibleToSupporter && styles.timelineTagInternal
													)}
												>
													{t(
														entry.visibleToSupporter
															? 'visibleToSupporter'
															: 'internalOnly'
													)}
												</span>
											</div>
										)}
									</div>
								</li>
							)
						})}
					</ol>
				)}
			</Card>
		</>
	)
}

SupporterLogTab.propTypes = {
	supporterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}
